import { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../api/client';
import { ROUTES } from '../../api/routes';
import { useAuthStore } from '../../store/authStore';
import { useAvatarUpload } from '../../hooks/useAvatarUpload';
import { UserAvatar } from '../../components/ui/UserAvatar';
import { PasskeySetupModal } from '../../components/security/PasskeySetupModal';
import { PushPermissionRequest } from '../../components/security/PushPermissionRequest';
import { Modal } from '../../components/ui/Modal';
import { saveAccountToLocal } from '../../auth/savedAccounts';
import { markPasskeyOnThisDevice } from '../../auth/passkeyDevice';
import {
  getPasskeyStatus,
  revokeCurrentDevicePasskeyWithExplicitConsent,
  revokeAllPasskeysWithExplicitConsent,
  PASSKEY_REVOKE_ALL_BLAST_RADIUS,
} from '../../security/passkey/passkeyApi';
import {
  Fingerprint, Shield, Camera, Smartphone, Laptop, Monitor, Key, Bell,
  Trash2, Clock, Globe, CheckCircle, AlertTriangle, Pencil, Save, X, HardDrive,
  School, Mail, User, ChevronRight, ChevronLeft, LogOut, Info, Loader2, Building2, MapPin, Network, Lock,
  Copy, Check, ChevronDown, ChevronUp, Home, FileText, Calendar, Phone
} from 'lucide-react';
import { formatCPF, formatPhone } from '../../utils/cpfUtils';
import { toast } from 'sonner';
import { ResponsavelTabBar } from '../../components/responsavel/ResponsavelTabBar';
import { MobileTabBar } from '../professor/MobileTabBar';
import {
  getUserDevices,
  revokeUserDevice,
  formatRelativeDeviceAccess,
  sortDevicesByMostRecent,
  type Dispositivo,
} from '../../utils/userDevices';

const API = import.meta.env.VITE_API_URL || '/api/v1';

export function PerfilPage() {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const logout = useAuthStore((s) => s.logout);
  const checkAuth = useAuthStore((s) => s.checkAuth);
  const nomeEscola = useAuthStore((s) => s.nomeEscola);

  // Avatar upload real hook with circular cropper
  const {
    isUploading: uploadingPhoto,
    openFilePicker: handleFotoClick,
    fileInputRef,
    handleInputChange: handleFotoChange,
    cropperModal,
  } = useAvatarUpload();

  // Modals & States (Mocked interaction)
  const [showPasskeyModal, setShowPasskeyModal] = useState(false);
  const [showPasskeyManagement, setShowPasskeyManagement] = useState(false);
  const [showPushModal, setShowPushModal] = useState(false);
  const [showSenhaModal, setShowSenhaModal] = useState(false);

  // Edit Email Modal States & Handlers
  const [showEditEmailModal, setShowEditEmailModal] = useState(false);
  const [novoEmailInput, setNovoEmailInput] = useState('');
  const [senhaAtualInput, setSenhaAtualInput] = useState('');
  const [otpAtualInput, setOtpAtualInput] = useState('');
  const [editEmailLoading, setEditEmailLoading] = useState(false);
  const [editEmailError, setEditEmailError] = useState('');
  const [editEmailErrorDetails, setEditEmailErrorDetails] = useState('');
  const [showEditErrorDetails, setShowEditErrorDetails] = useState(false);
  const [editErrorCopied, setEditErrorCopied] = useState(false);
  const [solicitandoOtpEmail, setSolicitandoOtpEmail] = useState(false);
  const [otpEmailEnviado, setOtpEmailEnviado] = useState(false);

  const extractApiErrorMessage = (err: any, fallback = 'Erro ao processar solicitação'): string => {
    const data = err?.response?.data;
    if (typeof data?.error?.message === 'string' && data.error.message.trim()) return data.error.message;
    if (typeof data?.message === 'string' && data.message.trim()) return data.message;
    if (Array.isArray(data?.message) && data.message.length > 0) return data.message.join(', ');
    if (typeof data?.error === 'string' && data.error.trim()) return data.error;
    if (typeof err?.message === 'string' && err.message.trim()) return err.message;
    return fallback;
  };

  const isEmailSintaticamenteValido = (email?: string) => {
    if (!email) return false;
    const clean = email.trim();
    return /^[^\s@]+@[^\s@]+\.[a-zA-Z]{2,}$/.test(clean) && !clean.endsWith('.mock');
  };

  const handleOpenEditEmail = () => {
    setShowEditEmailModal(true);
    setNovoEmailInput('');
    setSenhaAtualInput('');
    setOtpAtualInput('');
    setEditEmailError('');
    setEditEmailErrorDetails('');
    setShowEditErrorDetails(false);
    setOtpEmailEnviado(false);
  };

  const handleRequestOtpTrocaEmail = async () => {
    setSolicitandoOtpEmail(true);
    setEditEmailError('');
    setEditEmailErrorDetails('');
    try {
      await api.post('/auth/me/request-email-change-otp');
      setOtpEmailEnviado(true);
      toast.info('Código de autorização enviado para seu e-mail atual!');
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Erro ao enviar código de autorização.');
      setEditEmailError(msg);
      const details = {
        timestamp: new Date().toISOString(),
        endpoint: '/auth/me/request-email-change-otp',
        status: err.response?.status || 'CLIENT_OR_NETWORK_ERROR',
        statusText: err.response?.statusText || '',
        message: msg,
        targetEmail: user?.email || '',
        responseData: err.response?.data || null,
      };
      setEditEmailErrorDetails(JSON.stringify(details, null, 2));
    } finally {
      setSolicitandoOtpEmail(false);
    }
  };

  const handleSaveNovoEmail = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!novoEmailInput.trim() || !isEmailSintaticamenteValido(novoEmailInput)) {
      setEditEmailError('Informe um formato de e-mail válido (ex: usuario@dominio.com).');
      return;
    }
    if (novoEmailInput.trim().toLowerCase() === (user?.email || '').toLowerCase()) {
      setEditEmailError('O novo e-mail deve ser diferente do e-mail atual.');
      return;
    }
    if (emailVerificado) {
      if (!otpAtualInput.trim() || otpAtualInput.trim().length < 6) {
        setEditEmailError('Digite o código de autorização de 6 dígitos enviado ao seu e-mail atual.');
        return;
      }
    } else {
      if (!senhaAtualInput.trim()) {
        setEditEmailError('Digite sua senha de acesso atual para confirmar a alteração.');
        return;
      }
    }

    setEditEmailLoading(true);
    setEditEmailError('');
    setEditEmailErrorDetails('');
    try {
      const res = await api.patch('/auth/me/email', {
        novoEmail: novoEmailInput.trim().toLowerCase(),
        senhaAtual: !emailVerificado ? senhaAtualInput : undefined,
        codigoOtpAtual: emailVerificado ? otpAtualInput.trim() : undefined,
      });

      if (user) {
        const updatedUser = { ...user, email: res.data.email, emailVerificado: false };
        useAuthStore.setState({ user: updatedUser });
        try {
          localStorage.setItem('ac_user', JSON.stringify(updatedUser));
        } catch {}
      }
      setEmailVerificado(false);

      toast.success('E-mail alterado com sucesso! Agora você já pode confirmá-lo.');
      setShowEditEmailModal(false);
      setNovoEmailInput('');
      setSenhaAtualInput('');
      setOtpAtualInput('');
    } catch (err: any) {
      const msg = extractApiErrorMessage(err, 'Erro ao atualizar e-mail.');
      setEditEmailError(msg);
      const details = {
        timestamp: new Date().toISOString(),
        endpoint: '/auth/me/email',
        status: err.response?.status || 'CLIENT_OR_NETWORK_ERROR',
        statusText: err.response?.statusText || '',
        message: msg,
        targetNovoEmail: novoEmailInput.trim().toLowerCase(),
        responseData: err.response?.data || null,
      };
      setEditEmailErrorDetails(JSON.stringify(details, null, 2));
    } finally {
      setEditEmailLoading(false);
    }
  };

  // Passkey Status States
  const [loadingStatus, setLoadingStatus] = useState(true);
  const [hasPasskey, setHasPasskey] = useState(false);
  const [hasPasskeyDesktop, setHasPasskeyDesktop] = useState(false);
  const [hasPasskeyMobile, setHasPasskeyMobile] = useState(false);
  const [hasPushNotifications, setHasPushNotifications] = useState(false);
  const [emailVerificado, setEmailVerificado] = useState(user?.emailVerificado || false);

  useEffect(() => {
    const handleVerified = () => {
      setEmailVerificado(true);
      checkAuth().catch(() => {});
    };
    window.addEventListener('ac:email:verified', handleVerified);
    return () => window.removeEventListener('ac:email:verified', handleVerified);
  }, [checkAuth]);

  // States for password change authentication flow
  const [senhaModalStep, setSenhaModalStep] = useState<'verify_current' | 'new_password'>('verify_current');
  const [senhaAtualDigitada, setSenhaAtualDigitada] = useState('');
  const [novaSenha, setNovaSenha] = useState('');
  const [confirmarNovaSenha, setConfirmarNovaSenha] = useState('');
  const [erroSenha, setErroSenha] = useState('');
  const [salvandoSenha, setSalvandoSenha] = useState(false);

  const loadSecurityStatus = async () => {
    try {
      const status = await getPasskeyStatus();
      setHasPasskey(!!status.temPasskey);
      setHasPasskeyDesktop(!!status.temPasskeyDesktop);
      setHasPasskeyMobile(!!status.temPasskeyMobile);
    } catch (err) {
      console.error('Erro ao buscar status de biometria:', err);
    } finally {
      setLoadingStatus(false);
    }
  };

  useEffect(() => {
    loadSecurityStatus();
    if (typeof window !== 'undefined' && 'Notification' in window && typeof (window as any).Notification !== 'undefined') {
      setHasPushNotifications((window as any).Notification.permission === 'granted');
    }
    setEmailVerificado(!!useAuthStore.getState().user?.emailVerificado);
  }, []);

  const openSenhaModal = () => {
    // Se o usuário não tem senha cadastrada, pula direto para definir nova senha
    const temSenha = !!(user as any)?.hasSenha;
    setSenhaModalStep(temSenha ? 'verify_current' : 'new_password');
    setSenhaAtualDigitada('');
    setNovaSenha('');
    setConfirmarNovaSenha('');
    setErroSenha('');
    setSalvandoSenha(false);
    setShowSenhaModal(true);
  };

  // Celular Mestre: não implementado ainda neste fluxo — noop intencional

  // Edit states
  const [editingName, setEditingName] = useState(false);
  const [nameValue, setNameValue] = useState(user?.nome || 'Usuário');

  useEffect(() => {
    if (user?.nome) {
      setNameValue(user.nome);
    }
  }, [user?.nome]);

  // Active sessions / devices data sincronizado
  const [devices, setDevices] = useState<Dispositivo[]>(getUserDevices);

  useEffect(() => {
    const handleSync = () => setDevices(getUserDevices());
    window.addEventListener('ac:devices:updated', handleSync);
    return () => window.removeEventListener('ac:devices:updated', handleSync);
  }, []);

  const handleSaveName = async () => {
    if (!nameValue.trim()) {
      toast.error('O nome não pode estar vazio');
      return;
    }
    try {
      await api.patch(ROUTES.auth.me, { nome: nameValue.trim() });
      await checkAuth();
      setEditingName(false);
      toast.success('Nome atualizado com sucesso!');
    } catch (err: any) {
      toast.error(err.response?.data?.message || 'Erro ao salvar nome');
    }
  };

  const handleRevokeDevice = (id: string, name: string) => {
    if (confirm(`Deseja realmente desconectar o dispositivo "${name}"?`)) {
      const updated = revokeUserDevice(id);
      setDevices(updated);
      toast.success(`Sessão encerrada no dispositivo "${name}".`);
    }
  };

  const handleLogoutClick = async () => {
    if (confirm('Deseja realmente sair da sua conta?')) {
      await logout();
      navigate('/login');
    }
  };

  // Helper functions
  const isProfessor = user?.perfil === 'PROFESSOR';
  const iniciais = nameValue.split(' ').map(n => n[0]).slice(0, 2).join('').toUpperCase();

  const homeRoute = (() => {
    switch (user?.perfil) {
      case 'ADMIN': return '/master/dashboard';
      case 'RESPONSAVEL': return '/responsavel/dashboard';
      case 'PROFESSOR': return '/professor/dashboard';
      case 'ALUNO': return '/aluno/dashboard';
      case 'MUNICIPAL': return '/municipal/dashboard';
      case 'ESTADUAL': return '/estadual/dashboard';
      case 'REDE': return '/rede/dashboard';
      case 'ESCOLA': return '/admin/dashboard';
      default: return '/admin/dashboard';
    }
  })();

  return (
    <div className="w-full h-full overflow-y-auto px-4 py-6 md:py-8 pb-28 md:pb-8 font-sans antialiased text-slate-800">
      <div className="max-w-7xl mx-auto">
      
      {/* ── Header ── */}
      <header className="mb-8 space-y-2">
        <div className="flex items-center justify-between gap-2">
          <div className="flex items-center gap-1.5 sm:gap-2 min-w-0">
            <button
              onClick={() => navigate(-1)}
              type="button"
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200/60 shadow-sm flex-shrink-0"
              title="Voltar à página anterior"
            >
              <ChevronLeft size={18} />
            </button>
            <button
              onClick={() => navigate(homeRoute)}
              type="button"
              className="w-8 h-8 sm:w-9 sm:h-9 flex items-center justify-center rounded-xl bg-slate-100 hover:bg-slate-200 text-slate-700 transition-colors border border-slate-200/60 shadow-sm flex-shrink-0"
              title="Ir para o Início / Painel Principal"
            >
              <Home size={16} />
            </button>
            <h1 className="text-xl sm:text-2xl md:text-3xl font-extrabold tracking-tight text-slate-900 ml-1 whitespace-nowrap overflow-hidden text-ellipsis">
              Meu Perfil
            </h1>
          </div>
          <button
            onClick={handleLogoutClick}
            className="flex items-center justify-center gap-1.5 px-3 py-1.5 md:px-4 md:py-2 bg-rose-50 text-rose-600 hover:bg-rose-100 font-bold text-xs md:text-xs rounded-xl border border-rose-200/50 transition-colors flex-shrink-0"
          >
            <LogOut size={14} />
            <span className="md:hidden">Sair</span>
            <span className="hidden md:inline">Sair da Conta</span>
          </button>
        </div>
        <p className="text-xs md:text-sm text-slate-500">
          Gerencie seus dados pessoais, segurança e sessões ativas do aplicativo.
        </p>
      </header>

      {/* ── Main Layout Grid ── */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* ── Left Column: Profile Identity Card ── */}
        <div className="lg:col-span-1 space-y-6">
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm flex flex-col items-center text-center relative overflow-hidden">
            {/* Soft decorative background glow */}
            <div className="absolute top-0 inset-x-0 h-28 bg-gradient-to-br from-indigo-50/70 to-violet-50/70 -z-10" />
            
            {/* Avatar Section */}
            <div className="relative group cursor-pointer mt-4 flex flex-col items-center">
              <UserAvatar
                src={user?.fotoUrl}
                name={user?.nome}
                role={user?.cargo || user?.perfil}
                size="2xl"
                editable
                isUploading={uploadingPhoto}
                onEditClick={handleFotoClick}
                onClick={handleFotoClick}
              />
              <input 
                ref={fileInputRef}
                type="file"
                accept="image/*"
                className="hidden"
                onChange={handleFotoChange}
              />
              {cropperModal}
            </div>
            
            <p className="text-[10px] text-slate-400 mt-2">Toque para alterar imagem (JPG ou PNG, até 3MB)</p>

            {/* User Info details */}
            <div className="w-full mt-6 space-y-4">
              {editingName ? (
                <div className="flex items-center gap-2 bg-slate-50 border border-slate-200 rounded-xl p-1">
                  <input
                    type="text"
                    value={nameValue}
                    onChange={(e) => setNameValue(e.target.value)}
                    className="w-full bg-transparent px-3 py-2 text-sm font-semibold focus:outline-none"
                    autoFocus
                    onKeyDown={(e) => e.key === 'Enter' && handleSaveName()}
                  />
                  <button onClick={handleSaveName} className="p-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 transition-colors">
                    <Save size={14} />
                  </button>
                  <button onClick={() => setEditingName(false)} className="p-2 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors">
                    <X size={14} />
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-center gap-2">
                  <h2 className="text-xl font-extrabold text-slate-800 tracking-tight">{nameValue}</h2>
                  <button 
                    onClick={() => setEditingName(true)} 
                    className="p-1 text-slate-400 hover:text-indigo-600 rounded hover:bg-slate-55 transition-colors"
                    title="Editar nome"
                  >
                    <Pencil size={13} />
                  </button>
                </div>
              )}

              <span className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full font-bold text-[10px] uppercase tracking-wider">
                <Shield size={10} className="mr-1" />
                {user?.perfil || 'PROFESSOR'}
              </span>

              {/* ── Vínculo Institucional Dinâmico por Perfil ── */}
              {(() => {
                const perfil = (user?.perfil || 'ESCOLA').toUpperCase();
                const isPerfilMunicipal = perfil === 'MUNICIPAL';
                const isPerfilEstadual = perfil === 'ESTADUAL';
                const isPerfilRede = perfil === 'REDE';
                const isPerfilMaster = perfil === 'MASTER' || perfil === 'SISTEMA';
                const isPerfilEscola = !isPerfilMunicipal && !isPerfilEstadual && !isPerfilRede && !isPerfilMaster;

                const displayMunicipio = (user as any)?.municipioNome || (user as any)?.municipio?.nome || (user as any)?.escola?.cidade || (user as any)?.preferencias?.municipioNome || (isPerfilMunicipal ? 'Belo Horizonte - MG' : null);
                const displayEstado = (user as any)?.estadoNome || (user as any)?.estado?.sigla || (user as any)?.escola?.uf || (user as any)?.preferencias?.estadoNome || (isPerfilEstadual || isPerfilMunicipal ? 'Minas Gerais (MG)' : null);
                const displayRede = (user as any)?.redeNome || (user as any)?.rede?.nome || (user as any)?.preferencias?.redeNome || (isPerfilRede ? 'Rede Integrada de Ensino' : null);
                const displayEscola = nomeEscola || (user as any)?.nomeEscola || (isPerfilEscola ? 'Escola Municipal Ayrton Senna' : null);

                return (
                  <div className="border-t border-slate-100 pt-4 space-y-3 text-left">
                    {/* E-mail: Verificado vs Inválido vs Não Confirmado */}
                    {(() => {
                      const emailValido = isEmailSintaticamenteValido(user?.email);

                      if (!emailValido) {
                        return (
                          <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-2xl transition-all shadow-sm">
                            <div className="flex items-center justify-between gap-2 mb-1">
                              <div className="flex items-center gap-1.5 text-rose-700 min-w-0">
                                <AlertTriangle size={13} className="text-rose-600 flex-shrink-0" />
                                <span className="text-[10px] font-black uppercase tracking-wider truncate">E-mail Inválido</span>
                              </div>
                              <button
                                type="button"
                                onClick={handleOpenEditEmail}
                                className="px-2 py-0.5 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-1 flex-shrink-0 active:scale-95"
                              >
                                <span>Corrigir</span>
                              </button>
                            </div>
                            <p className="text-xs font-bold text-rose-950 truncate pl-4">
                              {user?.email || 'Sem e-mail cadastrado'}
                            </p>
                            <p className="text-[10px] text-rose-600 font-semibold pl-4 mt-0.5">
                              Formato inválido. Corrija para poder receber confirmação.
                            </p>
                          </div>
                        );
                      }

                      if (!emailVerificado) {
                        return (
                          <div className="p-3 bg-rose-50/90 border border-rose-200 rounded-2xl transition-all shadow-sm">
                            <div className="flex items-center justify-between gap-2 mb-1.5 flex-wrap">
                              <div className="flex items-center gap-1.5 text-rose-700">
                                <AlertTriangle size={13} className="text-rose-600 flex-shrink-0" />
                                <span className="text-[10px] font-black uppercase tracking-wider">E-mail não verificado</span>
                              </div>
                              <div className="flex items-center gap-1.5 flex-shrink-0">
                                <button
                                  type="button"
                                  onClick={handleOpenEditEmail}
                                  className="px-1.5 py-0.5 text-rose-700 hover:text-rose-950 text-[10px] font-bold underline transition-colors"
                                >
                                  Alterar
                                </button>
                                <button
                                  type="button"
                                  onClick={() => window.dispatchEvent(new CustomEvent('ac:email:verify:open'))}
                                  className="px-2.5 py-1 bg-rose-600 hover:bg-rose-700 text-white text-[10px] font-black uppercase tracking-wider rounded-lg shadow-sm hover:shadow transition-all flex items-center gap-1 flex-shrink-0 active:scale-95"
                                >
                                  <span>Confirmar</span>
                                </button>
                              </div>
                            </div>
                            <p className="text-xs font-bold text-rose-950 truncate pl-4">
                              {user?.email}
                            </p>
                          </div>
                        );
                      }

                      return (
                        <div className="flex items-center justify-between gap-2 text-slate-600">
                          <div className="flex items-center gap-3 min-w-0">
                            <Mail size={14} className="text-slate-400 flex-shrink-0" />
                            <div className="min-w-0">
                              <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">E-mail</p>
                              <p className="text-xs font-semibold truncate">{user?.email}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[9px] font-bold rounded-full border border-emerald-200 flex-shrink-0">
                              <CheckCircle size={10} className="text-emerald-600" />
                              Confirmado
                            </span>
                            <button
                              type="button"
                              onClick={handleOpenEditEmail}
                              className="text-[10px] text-slate-400 hover:text-indigo-600 font-bold underline transition-colors"
                              title="Alterar e-mail confirmado (exige autorização)"
                            >
                              Alterar
                            </button>
                          </div>
                        </div>
                      );
                    })()}

                    {/* CPF */}
                    {(() => {
                      const rawCpf = user?.cpf || (user as any)?.responsavelVinculado?.cpf;
                      if (!rawCpf) return null;
                      return (
                        <div className="flex items-center gap-3 text-slate-600">
                          <FileText size={14} className="text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">CPF</p>
                            <p className="text-xs font-semibold truncate">{formatCPF(rawCpf)}</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Data de Nascimento */}
                    {(() => {
                      const rawNasc = user?.dataNascimento || (user as any)?.responsavelVinculado?.dataNascimento;
                      if (!rawNasc) return null;
                      const dateFormatted = new Date(rawNasc).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
                      return (
                        <div className="flex items-center gap-3 text-slate-600">
                          <Calendar size={14} className="text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Data de Nascimento</p>
                            <p className="text-xs font-semibold truncate">{dateFormatted}</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Telefone / WhatsApp */}
                    {(() => {
                      const rawTel = user?.telefone || (user as any)?.responsavelVinculado?.telefone;
                      if (!rawTel) return null;
                      return (
                        <div className="flex items-center gap-3 text-slate-600">
                          <Phone size={14} className="text-slate-400 flex-shrink-0" />
                          <div className="min-w-0">
                            <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Telefone / WhatsApp</p>
                            <p className="text-xs font-semibold truncate">{formatPhone(rawTel)}</p>
                          </div>
                        </div>
                      );
                    })()}

                    {/* Master: Âmbito Global */}
                    {isPerfilMaster && (
                      <div className="flex items-center gap-3 text-slate-600">
                        <Globe size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="min-width-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Âmbito Institucional</p>
                          <p className="text-xs font-semibold truncate">Administração Master Global</p>
                        </div>
                      </div>
                    )}

                    {/* Perfis Escolares: Exibe Escola Ativa */}
                    {isPerfilEscola && displayEscola && (
                      <div className="flex items-center gap-3 text-slate-600">
                        <School size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="min-width-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Escola Ativa</p>
                          <p className="text-xs font-semibold truncate">{displayEscola}</p>
                        </div>
                      </div>
                    )}

                    {/* Perfis Municipal e Escolares: Exibe Município */}
                    {(isPerfilMunicipal || isPerfilEscola) && displayMunicipio && (
                      <div className="flex items-center gap-3 text-slate-600">
                        <Building2 size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="min-width-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Município</p>
                          <p className="text-xs font-semibold truncate">{displayMunicipio}</p>
                        </div>
                      </div>
                    )}

                    {/* Perfis Estadual, Municipal e Escolares: Exibe Estado */}
                    {(isPerfilEstadual || isPerfilMunicipal || isPerfilEscola) && displayEstado && (
                      <div className="flex items-center gap-3 text-slate-600">
                        <MapPin size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="min-width-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Estado</p>
                          <p className="text-xs font-semibold truncate">{displayEstado}</p>
                        </div>
                      </div>
                    )}

                    {/* Perfis Rede, Estadual, Municipal e Escolares: Exibe Rede Conectada */}
                    {(isPerfilRede || isPerfilEstadual || isPerfilMunicipal || isPerfilEscola) && displayRede && (
                      <div className="flex items-center gap-3 text-slate-600">
                        <Network size={14} className="text-slate-400 flex-shrink-0" />
                        <div className="min-width-0">
                          <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Rede Conectada</p>
                          <p className="text-xs font-semibold truncate">{displayRede}</p>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })()}
            </div>
          </div>
        </div>

        {/* ── Right Column: Configuration panels & Active Devices ── */}
        <div className="lg:col-span-2 space-y-6">

          {/* ── Security Configuration Buttons Panel ── */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4 border-b border-slate-50 pb-2">
              Segurança e Acesso
            </h3>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              
              {/* Row 1: Alterar Senha */}
              <button
                onClick={openSenhaModal}
                className="flex items-center justify-between p-4 rounded-2xl border border-slate-100 bg-slate-50/50 hover:bg-slate-50 text-left transition-colors group"
              >
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-xl bg-slate-100 text-slate-400 flex items-center justify-center flex-shrink-0">
                    <Key size={16} />
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">Alterar Senha</h4>
                    <p className="text-[10px] text-slate-400 font-medium">Senha local cadastrada e protegida</p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Row 2: Biometria */}
              <button
                onClick={() => hasPasskey ? setShowPasskeyManagement(true) : setShowPasskeyModal(true)}
                className={`flex items-center justify-between p-4 rounded-2xl border text-left transition-colors group ${
                  hasPasskey 
                    ? 'border-slate-100 bg-slate-50/50 hover:bg-slate-50' 
                    : 'border-amber-200 bg-amber-50/30 hover:bg-amber-50/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    hasPasskey 
                      ? 'bg-slate-100 text-slate-400' 
                      : 'bg-amber-50 text-amber-500 border border-amber-200/50'
                  }`}>
                    {hasPasskey ? <Fingerprint size={16} /> : <AlertTriangle size={16} />}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">Biometria / Passkey</h4>
                    <p className={`text-[10px] font-medium ${hasPasskey ? 'text-slate-400' : 'text-amber-600 font-bold'}`}>
                      {loadingStatus ? 'Verificando...' : hasPasskey ? '✓ Cadastrada e ativa' : '[Pendente] Cadastrar chave biométrica'}
                    </p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Row 3: Celular Mestre */}
              <button
                onClick={() => navigate('/onboarding/security?somenteVinculo=1')}
                className={`flex items-center justify-between p-4 rounded-2xl border text-left transition-colors group ${
                  hasPasskeyMobile 
                    ? 'border-slate-100 bg-slate-50/50 hover:bg-slate-50' 
                    : 'border-amber-200 bg-amber-50/30 hover:bg-amber-50/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    hasPasskeyMobile 
                      ? 'bg-slate-100 text-slate-400' 
                      : 'bg-amber-50 text-amber-500 border border-amber-200/50'
                  }`}>
                    {hasPasskeyMobile ? <Smartphone size={16} /> : <AlertTriangle size={16} />}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">Celular Mestre</h4>
                    <p className={`text-[10px] font-medium ${hasPasskeyMobile ? 'text-slate-400' : 'text-amber-600 font-bold'}`}>
                      {loadingStatus ? 'Verificando...' : hasPasskeyMobile ? '✓ Celular Mestre vinculado' : '[Pendente] Vincular Celular Mestre'}
                    </p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
              </button>

              {/* Row 4: Notificações Push */}
              <button
                onClick={() => setShowPushModal(true)}
                className={`flex items-center justify-between p-4 rounded-2xl border text-left transition-colors group ${
                  hasPushNotifications 
                    ? 'border-slate-100 bg-slate-50/50 hover:bg-slate-50' 
                    : 'border-amber-200 bg-amber-50/30 hover:bg-amber-50/60'
                }`}
              >
                <div className="flex items-center gap-3">
                  <div className={`w-9 h-9 rounded-xl flex items-center justify-center flex-shrink-0 ${
                    hasPushNotifications 
                      ? 'bg-slate-100 text-slate-400' 
                      : 'bg-amber-50 text-amber-500 border border-amber-200/50'
                  }`}>
                    {hasPushNotifications ? <Bell size={16} /> : <AlertTriangle size={16} />}
                  </div>
                  <div>
                    <h4 className="text-xs font-bold text-slate-800">Alertas e Notificações</h4>
                    <p className={`text-[10px] font-medium ${hasPushNotifications ? 'text-slate-400' : 'text-amber-600 font-bold'}`}>
                      {hasPushNotifications ? '✓ Notificações push ativas' : '[Pendente] Ativar notificações push'}
                    </p>
                  </div>
                </div>
                <ChevronRight size={14} className="text-slate-400 group-hover:translate-x-1 transition-transform" />
              </button>

            </div>
          </div>

          {/* ── Active Sessions / Connected Devices Panel ── */}
          <div className="bg-white border border-slate-100 rounded-3xl p-6 shadow-sm">
            <h3 className="text-sm font-black text-slate-900 uppercase tracking-widest mb-4 border-b border-slate-50 pb-2">
              Sessões Ativas e Dispositivos
            </h3>

            <div className="space-y-3">
              {sortDevicesByMostRecent(devices).map((device) => {
                const timeInfo = formatRelativeDeviceAccess(device.ultimoAcesso, device.esteDispositivo);
                return (
                  <div
                    key={device.id}
                    className={`flex flex-col sm:flex-row sm:items-center sm:justify-between p-4 rounded-2xl border transition-colors gap-3 ${
                      timeInfo.isInativo30Dias
                        ? 'border-rose-200 bg-rose-50/25 hover:bg-rose-50/50 shadow-sm'
                        : 'border-slate-55 bg-slate-50/30 hover:bg-slate-50/60'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                        timeInfo.isInativo30Dias ? 'bg-rose-100 text-rose-600' : 'bg-slate-100 text-slate-600'
                      }`}>
                        {device.sistemaOp?.toLowerCase().includes('ios') || device.sistemaOp?.toLowerCase().includes('android') ? (
                          <Smartphone size={18} />
                        ) : (
                          <Laptop size={18} />
                        )}
                      </div>
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-xs font-bold text-slate-800">
                            {device.nome || device.navegadorDetectado}
                          </span>
                          {device.esteDispositivo && (
                            <span className="px-2 py-0.5 bg-indigo-50 text-indigo-600 text-[8px] font-black rounded-full uppercase tracking-wider">
                              Dispositivo Atual
                            </span>
                          )}
                          {timeInfo.isInativo30Dias && (
                            <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-rose-100 text-rose-700 text-[8px] font-black rounded-full uppercase tracking-wider border border-rose-200">
                              <AlertTriangle size={9} className="text-rose-600" />
                              Inativo (+30 dias)
                            </span>
                          )}
                        </div>
                        <p className="text-[10px] text-slate-400 font-medium mt-0.5">
                          {device.navegadorDetectado} · {device.sistemaOp}
                        </p>
                      </div>
                    </div>

                    <div className="flex items-center justify-between sm:justify-end gap-6 border-t sm:border-t-0 border-slate-100 pt-2 sm:pt-0">
                      <div className="text-left sm:text-right text-[10px] font-medium space-y-0.5">
                        <p className={`flex items-center gap-1 ${timeInfo.isInativo30Dias ? 'text-rose-600 font-bold' : 'text-slate-500'}`}>
                          <Clock size={10} className={timeInfo.isInativo30Dias ? 'text-rose-500' : 'text-slate-400'} />
                          Acesso: {timeInfo.label}
                        </p>
                        <p className="flex items-center gap-1 text-slate-400">
                          <Globe size={10} />
                          IP: {device.ip}
                        </p>
                      </div>

                      {!device.esteDispositivo && (
                        <button
                          onClick={() => handleRevokeDevice(device.id, device.nome || device.navegadorDetectado)}
                          className={`p-2 rounded-xl transition-colors ${
                            timeInfo.isInativo30Dias
                              ? 'bg-rose-100 text-rose-700 hover:bg-rose-200'
                              : 'text-rose-500 hover:bg-rose-50'
                          }`}
                          title="Encerrar Sessão"
                        >
                          <Trash2 size={15} />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Warnings context */}
            <div className="mt-4 p-3 bg-amber-50/50 rounded-xl border border-amber-100 flex gap-2.5 text-[10px] text-amber-800">
              <Info size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
              <span>
                Encerrar uma sessão desconectará imediatamente o acesso no navegador ou aparelho correspondente.
              </span>
            </div>

          </div>

        </div>

      </div>

      {/* ── Passkey / Biometria Setup Modal ── */}
      {showPasskeyModal && (
        <PasskeySetupModal
          onClose={() => setShowPasskeyModal(false)}
          onSuccess={async () => {
            setShowPasskeyModal(false);
            if (user) {
              markPasskeyOnThisDevice(user.email);
              saveAccountToLocal(user);
            }
            await checkAuth();
            await loadSecurityStatus();
            toast.success('Chave de acesso cadastrada com sucesso!');
          }}
        />
      )}

      {/* ── Passkey Management Modal ── */}
      <Modal
        open={showPasskeyManagement}
        onClose={() => setShowPasskeyManagement(false)}
        title="Gerenciar Chaves Biométricas (Passkeys)"
      >
        <div className="space-y-4 p-2 text-slate-800">
          <p className="text-xs text-slate-500">
            Você possui chaves de acesso biométricas configuradas e ativas nesta conta. Elas permitem login seguro sem digitação de senha.
          </p>

          <div className="bg-slate-50 border border-slate-100 rounded-2xl p-4 space-y-3">
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-slate-500">Status Geral:</span>
              <span className="font-extrabold text-emerald-600 uppercase tracking-wider">✓ Ativa</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-slate-500">Acesso via Computador:</span>
              <span className="font-semibold">{hasPasskeyDesktop ? 'Disponível' : 'Não cadastrado'}</span>
            </div>
            <div className="flex justify-between items-center text-xs">
              <span className="font-bold text-slate-500">Acesso via Celular Mestre:</span>
              <span className="font-semibold">{hasPasskeyMobile ? 'Disponível' : 'Não cadastrado'}</span>
            </div>
          </div>

          <div className="space-y-2 pt-2">
            <button
              onClick={() => {
                setShowPasskeyManagement(false);
                setShowPasskeyModal(true);
              }}
              className="w-full flex items-center justify-center gap-2 px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs rounded-xl shadow-sm transition-all"
            >
              <Fingerprint size={14} />
              Cadastrar Outra Chave Biométrica
            </button>

            <button
              onClick={async () => {
                if (confirm('Deseja realmente remover a chave biométrica deste dispositivo?')) {
                  try {
                    await revokeCurrentDevicePasskeyWithExplicitConsent({
                      explicitConsent: true,
                      reason: 'Usuario removeu chave do dispositivo pelo perfil'
                    });
                    toast.success('Chave biométrica do dispositivo removida com sucesso!');
                    await loadSecurityStatus();
                    setShowPasskeyManagement(false);
                  } catch (err: any) {
                    toast.error(err.message || 'Erro ao remover chave biométrica.');
                  }
                }
              }}
              className="w-full px-4 py-2.5 border border-slate-200 text-rose-600 hover:bg-rose-50 font-bold text-xs rounded-xl transition-all"
            >
              Desativar Biometria neste Dispositivo
            </button>

            <button
              onClick={async () => {
                if (confirm('DANGER: Isso removerá TODAS as chaves de acesso de todos os seus dispositivos. Para entrar novamente, você precisará usar sua senha padrão. Deseja continuar?')) {
                  try {
                    await revokeAllPasskeysWithExplicitConsent({
                      explicitConsent: true,
                      acknowledgedBlastRadius: PASSKEY_REVOKE_ALL_BLAST_RADIUS,
                      reason: 'Usuario removeu todas as chaves pelo perfil'
                    });
                    toast.success('Todas as chaves biométricas foram revogadas.');
                    await loadSecurityStatus();
                    setShowPasskeyManagement(false);
                  } catch (err: any) {
                    toast.error(err.message || 'Erro ao revogar chaves biométricas.');
                  }
                }
              }}
              className="w-full px-4 py-2.5 bg-rose-50 text-rose-700 hover:bg-rose-100 border border-rose-200/30 font-bold text-xs rounded-xl transition-all"
            >
              Desativar Todas as Chaves (Revogação Geral)
            </button>
          </div>
        </div>
      </Modal>

      {/* ── Push Notification Request Modal ── */}
      <Modal
        open={showPushModal}
        onClose={() => setShowPushModal(false)}
        title="Ativar Notificações"
      >
        <PushPermissionRequest
          onSuccess={async () => {
            await checkAuth();
            toast.success('Notificações de segurança ativadas!');
            setTimeout(() => setShowPushModal(false), 800);
          }}
        />
      </Modal>

      {/* ── Change Password Modal (2-step: senha atual → nova senha) ── */}
      <Modal
        open={showSenhaModal}
        onClose={() => !salvandoSenha && setShowSenhaModal(false)}
        title="Alterar Senha de Acesso"
      >
        <div className="space-y-4 p-2 text-slate-800">

          {/* STEP 1: Confirmar senha atual */}
          {senhaModalStep === 'verify_current' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Para sua segurança, informe sua senha atual antes de definir uma nova.
              </p>
              <div>
                <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Senha Atual</label>
                <input
                  type="password"
                  autoFocus
                  placeholder="Digite sua senha atual"
                  value={senhaAtualDigitada}
                  onChange={(e) => { setSenhaAtualDigitada(e.target.value); setErroSenha(''); }}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && senhaAtualDigitada.trim()) {
                      setErroSenha('');
                      setSenhaModalStep('new_password');
                    }
                  }}
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500 focus:bg-white transition-colors"
                />
              </div>
              {erroSenha && <span className="text-[10px] text-rose-500 font-medium">{erroSenha}</span>}

              <div className="flex gap-2 justify-end pt-1">
                <button
                  onClick={() => setShowSenhaModal(false)}
                  className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors"
                >
                  Cancelar
                </button>
                <button
                  onClick={() => {
                    if (!senhaAtualDigitada.trim()) {
                      setErroSenha('Informe sua senha atual.');
                      return;
                    }
                    setErroSenha('');
                    setSenhaModalStep('new_password');
                  }}
                  className="px-4 py-2 bg-indigo-600 text-white rounded-xl text-xs font-bold hover:bg-indigo-700 transition-all shadow-sm hover:shadow"
                >
                  Continuar
                </button>
              </div>
            </div>
          )}

          {/* STEP 2: Nova senha */}
          {senhaModalStep === 'new_password' && (
            <div className="space-y-4">
              <p className="text-xs text-slate-500">
                Defina sua nova senha de acesso. Use no mínimo 8 caracteres.
              </p>

              <div className="space-y-3">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Nova Senha</label>
                  <input
                    type="password"
                    autoFocus
                    placeholder="Mínimo 8 caracteres"
                    value={novaSenha}
                    onChange={(e) => { setNovaSenha(e.target.value); setErroSenha(''); }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500 focus:bg-white transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-wider text-slate-400 mb-1">Confirmar Nova Senha</label>
                  <input
                    type="password"
                    placeholder="Repita a nova senha"
                    value={confirmarNovaSenha}
                    onChange={(e) => { setConfirmarNovaSenha(e.target.value); setErroSenha(''); }}
                    className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl text-xs focus:outline-none focus:border-indigo-500 focus:bg-white transition-colors"
                  />
                </div>
                {erroSenha && <span className="text-[10px] text-rose-500 font-medium">{erroSenha}</span>}
              </div>

              <div className="flex gap-2 justify-end pt-2">
                <button
                  disabled={salvandoSenha}
                  onClick={() => {
                    setErroSenha('');
                    const temSenha = !!(user as any)?.hasSenha;
                    setSenhaModalStep(temSenha ? 'verify_current' : 'new_password');
                  }}
                  className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors disabled:opacity-50"
                >
                  Voltar
                </button>
                <button
                  disabled={salvandoSenha}
                  onClick={async () => {
                    if (!novaSenha || novaSenha.length < 8) {
                      setErroSenha('A senha deve ter no mínimo 8 caracteres.');
                      return;
                    }
                    if (novaSenha !== confirmarNovaSenha) {
                      setErroSenha('As senhas não coincidem.');
                      return;
                    }
                    setSalvandoSenha(true);
                    setErroSenha('');
                    try {
                      const temSenha = !!(user as any)?.hasSenha;
                      await api.patch('/auth/me/password', {
                        senha: novaSenha,
                        ...(temSenha && senhaAtualDigitada ? { senhaAtual: senhaAtualDigitada } : {}),
                      });
                      setShowSenhaModal(false);
                      setNovaSenha('');
                      setConfirmarNovaSenha('');
                      setSenhaAtualDigitada('');
                      setErroSenha('');
                      toast.success('Senha atualizada com sucesso!');
                    } catch (err: any) {
                      setErroSenha(extractApiErrorMessage(err, 'Erro ao alterar senha.'));
                    } finally {
                      setSalvandoSenha(false);
                    }
                  }}
                  className="px-4 py-2 bg-slate-900 text-white rounded-xl text-xs font-bold hover:bg-slate-800 transition-all shadow-sm hover:shadow disabled:opacity-50 flex items-center gap-2"
                >
                  {salvandoSenha && <Loader2 size={12} className="animate-spin" />}
                  {salvandoSenha ? 'Salvando...' : 'Confirmar Alteração'}
                </button>
              </div>
            </div>
          )}

        </div>
      </Modal>

      {/* ── Modal de Alteração de E-mail com Governança de Segurança ── */}
      <Modal
        open={showEditEmailModal}
        onClose={() => {
          if (!editEmailLoading) {
            setShowEditEmailModal(false);
          }
        }}
        title="Alterar Endereço de E-mail"
      >
        <div className="p-2 space-y-4 text-slate-800">
          <div className="flex items-center gap-3 p-3 bg-slate-50 border border-slate-200 rounded-xl text-slate-700 text-xs">
            <Mail size={18} className="text-indigo-600 flex-shrink-0" />
            <div>
              <p className="font-bold text-[11px] text-slate-400 uppercase tracking-wider">E-mail atual cadastrado</p>
              <p className="text-slate-900 font-extrabold text-xs">{user?.email || 'Nenhum e-mail registrado'}</p>
            </div>
          </div>

          {emailVerificado ? (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-xl text-amber-900 text-xs space-y-2">
              <div className="flex items-center gap-2 font-bold">
                <Shield size={16} className="text-amber-700 flex-shrink-0" />
                <span>Conta com e-mail confirmado</span>
              </div>
              <p className="text-[11px] leading-relaxed text-amber-800">
                Por segurança, para alterar um e-mail já confirmado, enviamos um código de autorização de 6 dígitos para o endereço atual (<strong>{user?.email}</strong>).
              </p>
              {!otpEmailEnviado ? (
                <button
                  type="button"
                  onClick={handleRequestOtpTrocaEmail}
                  disabled={solicitandoOtpEmail}
                  className="px-3 py-1.5 bg-amber-600 hover:bg-amber-700 text-white rounded-lg text-xs font-bold shadow-sm transition-all flex items-center gap-1.5"
                >
                  {solicitandoOtpEmail ? <Loader2 size={12} className="animate-spin" /> : <Mail size={12} />}
                  Enviar Código para E-mail Atual
                </button>
              ) : (
                <span className="text-[11px] text-emerald-700 font-bold flex items-center gap-1">
                  <CheckCircle size={12} /> Código de autorização enviado para {user?.email}!
                </span>
              )}
            </div>
          ) : (
            <div className="p-3 bg-indigo-50 border border-indigo-100 rounded-xl text-indigo-900 text-xs">
              <p className="font-bold flex items-center gap-1.5 mb-1 text-indigo-950">
                <Lock size={14} className="text-indigo-600 flex-shrink-0" />
                Confirmação de Identidade por Senha
              </p>
              <p className="text-[11px] leading-relaxed text-indigo-800">
                Como seu e-mail ainda não foi confirmado, confirme sua <strong>senha de acesso</strong> para autorizar a alteração do endereço da conta.
              </p>
            </div>
          )}

          <form onSubmit={handleSaveNovoEmail} className="space-y-3">
            {emailVerificado && (
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Código de Autorização (6 dígitos recebidos no e-mail atual)
                </label>
                <input
                  type="text"
                  maxLength={6}
                  value={otpAtualInput}
                  onChange={(e) => {
                    setOtpAtualInput(e.target.value.replace(/\D/g, ''));
                    setEditEmailError('');
                  }}
                  placeholder="0 0 0 0 0 0"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-bold tracking-widest text-center focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-sm"
                  required
                />
              </div>
            )}

            {!emailVerificado && (
              <div className="space-y-1">
                <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                  Sua Senha de Acesso Atual
                </label>
                <input
                  type="password"
                  value={senhaAtualInput}
                  onChange={(e) => {
                    setSenhaAtualInput(e.target.value);
                    setEditEmailError('');
                  }}
                  placeholder="Digite sua senha de login"
                  className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-medium focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-xs"
                  required
                />
              </div>
            )}

            <div className="space-y-1">
              <label className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                Novo Endereço de E-mail
              </label>
              <input
                type="email"
                value={novoEmailInput}
                onChange={(e) => {
                  setNovoEmailInput(e.target.value);
                  setEditEmailError('');
                }}
                placeholder="exemplo@dominio.com.br"
                className="w-full px-3 py-2 bg-slate-50 border border-slate-200 rounded-xl font-medium focus:outline-none focus:border-indigo-500 focus:bg-white transition-all text-xs"
                required
                autoFocus
              />
            </div>

            {editEmailError && (
              <div className="p-2.5 bg-rose-50 border border-rose-200 rounded-xl space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <div className="flex items-center gap-1.5 text-rose-700 text-xs font-semibold">
                    <AlertTriangle size={14} className="flex-shrink-0 text-rose-600" />
                    <span>{editEmailError}</span>
                  </div>
                  {editEmailErrorDetails && (
                    <button
                      type="button"
                      onClick={() => setShowEditErrorDetails(!showEditErrorDetails)}
                      className="text-[10px] font-bold text-rose-800 hover:text-rose-950 flex items-center gap-0.5 px-1.5 py-0.5 rounded bg-rose-100/60 hover:bg-rose-100 transition-colors"
                    >
                      {showEditErrorDetails ? <ChevronUp size={11} /> : <ChevronDown size={11} />}
                      {showEditErrorDetails ? 'Ocultar' : 'Detalhes'}
                    </button>
                  )}
                </div>

                {showEditErrorDetails && editEmailErrorDetails && (
                  <div className="pt-2 border-t border-rose-200/80 space-y-1.5">
                    <div className="flex items-center justify-between">
                      <span className="text-[9px] font-bold uppercase tracking-wider text-rose-900">Diagnóstico Técnico</span>
                      <button
                        type="button"
                        onClick={() => {
                          navigator.clipboard.writeText(editEmailErrorDetails);
                          setEditErrorCopied(true);
                          toast.success('Detalhes do erro copiados!');
                          setTimeout(() => setEditErrorCopied(false), 2000);
                        }}
                        className="text-[9px] font-bold text-rose-900 bg-white border border-rose-300 rounded px-2 py-0.5 flex items-center gap-1 hover:bg-rose-50 transition-all active:scale-95"
                      >
                        {editErrorCopied ? <Check size={10} className="text-emerald-600" /> : <Copy size={10} />}
                        {editErrorCopied ? 'Copiado!' : 'Copiar Detalhes'}
                      </button>
                    </div>
                    <pre className="p-2 bg-slate-900 text-slate-50 text-[10px] font-mono rounded-lg max-h-28 overflow-y-auto whitespace-pre-wrap break-all leading-tight">
                      {editEmailErrorDetails}
                    </pre>
                  </div>
                )}
              </div>
            )}

            <div className="flex gap-2 justify-end pt-2">
              <button
                type="button"
                onClick={() => setShowEditEmailModal(false)}
                className="px-4 py-2 border border-slate-200 text-slate-600 rounded-xl text-xs font-bold hover:bg-slate-50 transition-colors"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={editEmailLoading}
                className="px-5 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl text-xs font-extrabold shadow-sm transition-all flex items-center gap-1.5 disabled:opacity-50"
              >
                {editEmailLoading ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle size={13} />}
                Salvar Novo E-mail
              </button>
            </div>
          </form>
        </div>
      </Modal>

      </div>

      {/* ── Fixed Mobile Navigation Tab Bar ── */}
      {user?.perfil === 'RESPONSAVEL' && <ResponsavelTabBar activeTab="perfil" />}
      {user?.perfil === 'PROFESSOR' && <MobileTabBar hasPendencias={false} />}
    </div>
  );
}
